/**
 * "Applications you've sent" on Your Plan, kept apart from the live plan figures so terms as sent never
 * mix with live numbers. Each application shows its coverage as submitted, the date, an "Application
 * submitted" badge, what happens next, and "Review application". An application whose option is no
 * longer in the plan (removed or replaced since) says so: it stays with our team as sent.
 */
import { Card, CardContent } from '@design-system';
import { applicationsOutsideFundingPlan } from '@shared/consumerPlan';
import { money, PAYMENT_SCHEDULE_LABELS } from '../lib/format';
import { ApplicationReviewDialog } from './application-review-dialog';
import { Checklist } from './checklist';
import { DetailList } from './figures';
import { PolicyStatusBadge } from './policy-status-badge';

const NEXT_STEPS = [
  {
    marker: 'done' as const,
    title: 'Application sent to our team',
    description: 'We have everything you sent.',
  },
  {
    marker: 1,
    title: 'We check for errors and missing details',
    description: 'To help avoid delays before carrier submission',
  },
  {
    marker: 2,
    title: 'We coordinate underwriting with the carrier',
    description: 'Including the preliminary eligibility inquiry',
  },
  {
    marker: 3,
    title: 'We handle formal carrier submission',
    description: 'The carrier decides eligibility and final terms',
  },
];

const submittedOn = (submittedAt: string) =>
  new Date(submittedAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

interface ApplicationsSentSectionProps {
  clientId: string;
  applications: readonly ConsumerApplicationSubmission[];
  selectedQuoteKey: string | null;
}

export function ApplicationsSentSection({
  clientId,
  applications,
  selectedQuoteKey,
}: ApplicationsSentSectionProps) {
  if (applications.length === 0) return null;
  const outside = new Set(
    applicationsOutsideFundingPlan(applications, selectedQuoteKey).map(
      application => application.id,
    ),
  );

  return (
    <section
      className="flex flex-col gap-3"
      data-testid="applications-sent"
    >
      <h2 className="text-lg font-semibold text-gray-950">Applications you’ve sent</h2>
      {applications.map(({ id, submittedAt, coverage }) => (
        <Card
          key={id}
          className="py-4"
          data-testid="application-submitted-card"
        >
          <CardContent className="flex flex-col gap-4 px-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong className="text-gray-950">
                {coverage.carrierName} · {coverage.productName}
              </strong>
              <PolicyStatusBadge status="application-submitted" />
            </div>
            <p className="text-muted-foreground text-sm">
              Sent {submittedOn(submittedAt)}. Coverage isn’t active yet.
            </p>
            {outside.has(id) && (
              <p className="text-sm text-gray-950">
                Not counted in your plan. Your application is still with our team.
              </p>
            )}
            <DetailList
              rows={[
                {
                  label: 'Premium',
                  value: `${money(coverage.premium)} · ${PAYMENT_SCHEDULE_LABELS[coverage.paymentSchedule]}`,
                },
                {
                  label: 'Projected policy payout, as submitted',
                  value: money(coverage.projectedPayout),
                },
                {
                  label: 'Left for you to cover, as submitted',
                  value: money(coverage.outOfPocket),
                },
              ]}
            />
            <Checklist items={NEXT_STEPS} />
            <ApplicationReviewDialog
              clientId={clientId}
              applicationId={id}
            />
          </CardContent>
        </Card>
      ))}
    </section>
  );
}
